// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * Talking to Excel.
 *
 * The grid already had copy, cut, paste and duplicate -- but all of it moved
 * rows between places inside this app. Nothing it copied reached the system
 * clipboard, and nothing from the system clipboard reached it. A sub sends
 * forty quantities in a spreadsheet and there was no way to get them in
 * except by typing them.
 *
 * That is most of an estimator's day. They live in Excel, and a tool that
 * replaces part of Excel has to accept and emit Excel's own currency, which
 * is tab-separated text on the clipboard: rows on newlines, columns on tabs.
 *
 * This file is the translation, kept away from React so the awkward parts --
 * what "(1,200)" means, what an empty cell means, what happens when more is
 * pasted than was selected -- are decided once and tested.
 */

/**
 * Columns a paste can land in, left to right as the grid shows them.
 *
 * The order is the grid's, not a convenience: a block pasted at Qty spreads
 * into Unit and Unit Cost because that is what sits to the right of Qty on
 * screen, so what the user sees arriving is what they saw leaving Excel.
 *
 * Description is absent because the grid has no cell selection on it -- only
 * Qty, Unit Cost, Total and Contingency can be clicked as cells -- so it
 * could never be an anchor, and a paste never spreads leftward. Listing it
 * would describe a capability that does not exist. Total and Contingency are
 * absent for a different reason: Total is qty x unitCost and Contingency is a
 * mode plus a rate, so a number written into either would not survive the
 * next recalculation.
 */
export const PASTEABLE_FIELDS = ["qty", "unit", "unitCost"] as const;
export type PasteableField = (typeof PASTEABLE_FIELDS)[number];

export const isPasteable = (f: string): f is PasteableField =>
  (PASTEABLE_FIELDS as readonly string[]).includes(f);

/** Fields holding a number, so a paste into them is parsed rather than stored as text. */
const NUMERIC: ReadonlySet<string> = new Set(["qty", "unitCost"]);
export const isNumericField = (f: string) => NUMERIC.has(f);

/**
 * A number as a spreadsheet might have written it.
 *
 * Handles the currency symbol, thousands separators, a trailing percent, and
 * parentheses for negative -- which is not an edge case here, since this app
 * prints its own negatives that way and round-tripping its own output is the
 * first thing anyone will try.
 *
 * Returns null for anything that is not a number, so a header row pasted by
 * accident lands as "not a number" rather than as zero. Zero is a price;
 * nothing is not.
 */
export function parseNumeric(raw: string): number | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) { negative = true; s = s.slice(1, -1); }
  s = s.replace(/[$\s,]/g, "").replace(/%$/, "");
  if (s.startsWith("-")) { negative = !negative; s = s.slice(1); }
  if (!/^\d*\.?\d+$/.test(s)) return null;
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/**
 * Clipboard text as a grid.
 *
 * Excel ends a copied block with a trailing newline; that is a terminator and
 * not an empty final row, so dropping one trailing blank line is correct.
 * Blank lines in the MIDDLE are kept -- they are real empty cells, and
 * silently closing the gap would shift every value below them onto the wrong
 * line, which is the worst thing this function could do.
 */
export function parseClipboardGrid(text: string): string[][] {
  if (!text) return [];
  const body = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\n$/, "");
  if (!body) return [];
  return body.split("\n").map(line => line.split("\t"));
}

/** The inverse: a block of cells as Excel expects to receive it. */
export function toClipboardGrid(rows: Array<Array<string | number | null | undefined>>): string {
  return (rows || [])
    .map(r => (r || []).map(c => (c == null ? "" : String(c))).join("\t"))
    .join("\n");
}

export interface PasteCell {
  field: PasteableField;
  /** Parsed for numeric fields; the raw string otherwise. */
  value: number | string;
}

export interface PastePlan {
  /** What to write, keyed by row id then field. */
  edits: Record<string, Partial<Record<PasteableField, number | string>>>;
  /** How many rows actually received something. */
  rowsWritten: number;
  /** Pasted rows with no row left to land on. */
  rowsDropped: number;
  /** Pasted columns that ran past the last pasteable field. */
  colsDropped: number;
  /** Cells skipped because the text was not a number for a numeric field. */
  notNumeric: number;
}

/**
 * Work out what a paste would do, without doing it.
 *
 * Fills DOWN from the anchor, the way a spreadsheet does, and keeps going
 * past the end of the selection into the rows below it -- selecting one cell
 * and pasting forty values is the normal gesture, not an error.
 *
 * What it will not do is create rows. Running off the end of the estimate
 * reports `rowsDropped` so the caller can say how many did not fit; inventing
 * line items to catch overflow would add priced scope nobody asked for.
 */
export function planPaste(
  grid: string[][],
  startField: string,
  anchorRowId: string,
  /** Every row the paste may touch, in the order the grid shows them. */
  orderedRowIds: string[],
): PastePlan {
  const empty: PastePlan = { edits: {}, rowsWritten: 0, rowsDropped: 0, colsDropped: 0, notNumeric: 0 };
  if (!grid?.length || !isPasteable(startField)) return empty;

  const startCol = PASTEABLE_FIELDS.indexOf(startField);
  const startRow = (orderedRowIds || []).indexOf(anchorRowId);
  if (startRow === -1) return empty;

  const plan: PastePlan = { edits: {}, rowsWritten: 0, rowsDropped: 0, colsDropped: 0, notNumeric: 0 };
  let colsDroppedPerRow = 0;

  grid.forEach((cells, r) => {
    const rowId = orderedRowIds[startRow + r];
    if (!rowId) { plan.rowsDropped++; return; }
    let wrote = false;
    cells.forEach((raw, c) => {
      const field = PASTEABLE_FIELDS[startCol + c];
      if (!field) { colsDroppedPerRow = Math.max(colsDroppedPerRow, cells.length - (PASTEABLE_FIELDS.length - startCol)); return; }
      // An empty cell inside a pasted block clears nothing: Excel's own paste
      // writes the blank, but here that would wipe a price because the source
      // sheet happened to have a gap. Leaving it alone is recoverable; wiping
      // a column of unit costs is the kind of thing you find out about later.
      if (raw === "" || raw == null) return;
      if (isNumericField(field)) {
        const n = parseNumeric(raw);
        if (n == null) { plan.notNumeric++; return; }
        (plan.edits[rowId] ||= {})[field] = n;
      } else {
        (plan.edits[rowId] ||= {})[field] = String(raw).trim();
      }
      wrote = true;
    });
    if (wrote) plan.rowsWritten++;
  });

  plan.colsDropped = colsDroppedPerRow;
  return plan;
}

/** A sentence for the toast, or null when it all landed. */
export function describePaste(plan: PastePlan): string | null {
  const bits: string[] = [];
  if (plan.rowsDropped > 0) bits.push(`${plan.rowsDropped} row${plan.rowsDropped === 1 ? "" : "s"} past the end of the estimate`);
  if (plan.colsDropped > 0) bits.push(`${plan.colsDropped} column${plan.colsDropped === 1 ? "" : "s"} past the last pasteable field`);
  if (plan.notNumeric > 0) bits.push(`${plan.notNumeric} cell${plan.notNumeric === 1 ? "" : "s"} that were not numbers`);
  return bits.length ? `Skipped ${bits.join(", ")}` : null;
}
